import { useCallback, useEffect, useState, useRef } from 'react';
import { useAgentFileActions } from '../../context/AgentFileActionsContext.jsx';
import { uploadAgentJsonFile, downloadAgentJson } from '../agent/agentJsonFiles';
import { styled } from '@mui/joy/styles';
import { Box, Button, Grid, Sheet, Input, Textarea, Typography, Select, Option } from '@mui/joy';
import CallEndIcon from '@mui/icons-material/CallEnd';
import Fab from '@mui/material/Fab';
import {
  createAgent,
  createAgentDefinition,
  listAgents,
  listModels,
  updateAgent,
  stopAgent,
  listModelVoiceLocales,
  listModelVoicesForLocale,
  listModelRecognitionForLocale,
  getAgent,
} from '../../api/agent';
import FunctionCalls from '../functions/FunctionCalls';
import SelectAgent from '../agent/SelectAgent';
import SelectVoice from '../agent/SelectVoice';
import AuxSttToggle, { auxSttFromOptions, outputSttFromOptions, withAuxStt, withOutputStt } from '../agent/AuxSttToggle';
import Transcript from '../agent/Transcript';
import TemperatureSlider from '../agent/TemperatureSlider';
import PromptInput from '../agent/PromptInput';
import AgentButton from '../agent/AgentButton';
import AgentPersistenceControls from '../agent/AgentPersistenceControls';
import ExampleFiles from '../agent/ExampleFiles';
import TelephonyResourceSelector from '../agent/TelephonyResourceSelector';
import OutboundCallSetup from '../agent/OutboundCallSetup';
import Tooltip from '../common/Tooltip';

/** Prefer `{ error }` from API, then validation arrays, then `message`. */
function formatAgentApiError(err) {
  const data = err?.response?.data;
  if (data && typeof data === 'object' && data.error) return data.error;
  if (Array.isArray(data)) return data.join(', ');
  if (typeof data === 'string') return data;
  if (data?.message) return data.message;
  return err?.message || 'Request failed';
}

function normalizeAgentDisplayNameKey(name) {
  return (name != null ? String(name).trim() : '').toLowerCase();
}

const Item = styled(Sheet)(({ theme }) => ({
  backgroundColor: 'transparent',
  ...theme.typography.body2,
  padding: theme.spacing(1),
  textAlign: 'center',
  borderRadius: 4,
  color: theme.vars.palette.text.secondary,
}));

function useComponentWillUnmount(cleanupCallback = () => { }) {
  const callbackRef = useRef(cleanupCallback);
  callbackRef.current = cleanupCallback; // always up to date
  useEffect(() => {
    return () => callbackRef.current();
  }, []);
}

const defaultOptions = {
  tts: {
    vendor: 'google',
    language: 'en-GB',
    voice: 'en-GB-Wavenet-A'
  },
  stt: {
    vendor: 'google',
    language: 'en-GB'
  }

};

export default function LlmPanel({ error, setError, inform, setInform, status, selectedAgentId, setSelectedAgentId, syncToAgentId, isBuildTabActive = true, ...props }) {
  let [agent, setAgent] = useState({});
  let [agents, setAgents] = useState([]);
  let [initialising, setInitialising] = useState(false);
  let [audioModel, setAudioModel] = useState();
  let [changed, setChanged] = useState(false);
  let [functions, setFunctions] = useState([]);
  let [mcpServers, setMcpServers] = useState([]);
  let [apiKeys, setApiKeys] = useState([]);
  let [functionSupport, setFunctionSupport] = useState(false);
  let [mcpSupport, setMcpSupport] = useState(false);
  let [language, setLanguage] = useState(defaultOptions.tts.language);
  let [modelName, setModelName] = useState();
  let [options, setOptions] = useState(defaultOptions);
  let [prompt, setPrompt] = useState({});
  let [state, setState] = useState('initial');
  let [temperature, setTemperature] = useState(0.2);
  let [tooltip, setTooltip] = useState({});
  let [transcript, setTranscript] = useState();
  let [voice, setVoice] = useState(defaultOptions.tts.voice);
  let [voices, setVoices] = useState({});
  /** Locales from GET /models/{modelName}/voices (replaces inferring languages from flat /voices). */
  let [voiceLocales, setVoiceLocales] = useState(null);
  /** STT provider options from GET /models/{modelName}/recognition/{locale}. */
  let [sttProviders, setSttProviders] = useState([]);
  /** Currently selected STT vendor (e.g. 'deepgram', 'assemblyai'). */
  let [sttVendor, setSttVendor] = useState(defaultOptions.stt.vendor);
  /** Auxiliary transcription (options.stt.aux) — on by default; absent in a stored agent reads as on. */
  let [auxStt, setAuxStt] = useState(true);
  /** Output audit transcription (options.tts.output) — off by default; it adds cost. */
  let [outputStt, setOutputStt] = useState(false);
  let [ws, setWs] = useState();
  let [metaData, setMetaData] = useState({ name: '', description: '' });
  /** Persistent agent id when definition was loaded or saved (unchanged by disconnect). */
  let [agentDefinitionId, setAgentDefinitionId] = useState(null);
  let [showPhoneSelector, setShowPhoneSelector] = useState(false);
  let [showOutboundSetup, setShowOutboundSetup] = useState(false);
  let [pendingCallType, setPendingCallType] = useState(null);
  let [selectedPhoneNumber, setSelectedPhoneNumber] = useState(null);
  let [selectedEndpointId, setSelectedEndpointId] = useState(null);
  /** True when another agent already uses the current name (debounced server check). */
  let [nameIsDuplicate, setNameIsDuplicate] = useState(false);
  const { register: registerFileActions } = useAgentFileActions();

  /** Progressive onboarding tips — hide when Build tab is not visible (TabPanel is keepMounted). */
  const onboardingTooltip = (slot) => (isBuildTabActive ? tooltip[slot] : null);

  // Failover controls (behind ?failover URL flag)
  const [fallbackModel, setFallbackModel] = useState('');
  const [fallbackNumber, setFallbackNumber] = useState('');

  const [greetingKind, setGreetingKind] = useState('none'); // none | text | instructions
  const [greetingValue, setGreetingValue] = useState('');

  const applyGreetingToOptions = useCallback(
    (kind, value) => {
      const k = String(kind || 'none');
      const v = String(value || '');
      setOptions((prev) => {
        const base = { ...(prev || {}) };
        if (k === 'none') {
          const { greeting, ...rest } = base;
          return rest;
        }
        // Only write greeting when there is content (keeps API payload clean).
        if (!v.trim()) {
          const { greeting, ...rest } = base;
          return rest;
        }
        return k === 'instructions'
          ? { ...base, greeting: { instructions: v } }
          : { ...base, greeting: { text: v } };
      });
      setChanged(true);
    },
    [setOptions],
  );

  const enableFailover = (() => {
    try {
      const params = new URLSearchParams(window.location.search);
      return params.has('failover');
    } catch {
      return false;
    }
  })();

  useEffect(() => {
    const tryFetch = () => {
      !agents.length && listModels()
        .then(a => {
          setAgents(Object.fromEntries(a));
          setError(null);
        })
        .catch(err => {
          setError(`Couldn't communicate with server: ${err.message}`);
          setTimeout(tryFetch, 10000);
        });
    };
    setInitialising(true);
    !agents.length && !initialising && tryFetch();
  }, [initialising, agents.length, setError, setAgents, setInitialising]);

  useEffect(() => {
    if (!modelName) {
      setVoiceLocales(null);
      setVoices({});
      setSttProviders([]);
      return;
    }
    setVoiceLocales(null);
    let cancelled = false;
    listModelVoiceLocales(modelName)
      .then(({ locales }) => {
        if (!cancelled) setVoiceLocales(Array.isArray(locales) ? locales : []);
      })
      .catch((err) => {
        if (!cancelled) setError(formatAgentApiError(err));
      });
    return () => {
      cancelled = true;
    };
  }, [modelName, setError]);

  useEffect(() => {
    if (!modelName || !language) {
      return;
    }
    const handler = modelName.split(':')[0];
    let cancelled = false;
    listModelVoicesForLocale(modelName, language)
      .then(({ vendors }) => {
        if (cancelled) return;
        const v = vendors || {};
        setVoices({
          [handler]: Object.fromEntries(
            Object.entries(v).map(([vendor, arr]) => [vendor, { [language]: arr }]),
          ),
        });
      })
      .catch((err) => {
        if (!cancelled) setError(formatAgentApiError(err));
      });
    return () => {
      cancelled = true;
    };
  }, [modelName, language, setError]);

  useEffect(() => {
    if (!modelName || !language) {
      setSttProviders([]);
      return;
    }
    let cancelled = false;
    listModelRecognitionForLocale(modelName, language)
      .then(({ providers }) => {
        if (cancelled) return;
        const list = Array.isArray(providers) ? providers : [];
        setSttProviders(list);
        if (list.length && !list.some((p) => p.name === sttVendor)) {
          setSttVendor(list[0].name);
        }
      })
      .catch(() => {
        if (!cancelled) setSttProviders([]);
      });
    return () => {
      cancelled = true;
    };
  // sttVendor intentionally excluded — only re-run when model/language changes
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [modelName, language]);

  const disconnect = useCallback(async () => {
    if (state === 'active' && agent?.id) {
      setState('trying');
      await stopAgent(agent.id);
      setState('initial');
      setAgent(null);
      setWs(null);
      setState('initial');
    }
  }, [state, agent]);


  useEffect(() => {
    if (status !== 'loggedIn') {
      disconnect();
    }
    return () => {
      disconnect();
    };
  }, [status, disconnect]);


  useEffect(() => {
    if (!ws && state === 'active') {
      setError('Server disconnected, re-create agent to continue');
      disconnect();
    }
  }, [ws, state, disconnect, setError]);

  // manange progressive tooltip first time through UI
  useEffect(() => {
    if (!tooltip.done) {
      !modelName && setTooltip({
        selectAgent: { step: 1, title: 'Select model', text: 'This is the AI provider model that your agent will run' }
      });
      modelName && !prompt.value?.length && setTooltip({
        promptInput: { step: 2, title: 'Write your prompt', text: 'Personalise your agent here, the pre-filled text is a test prompt with a structure that we know works. Build your own following the structure or try something completely different!' }
      });
      modelName && prompt.changed && state === 'initial' && setTooltip({
        agentButton: {
          step: 3, title: 'Create agent',
          text: audioModel ? 'Talk to your agent' : 'Set things up to call your agent'
        }
      });
      state === 'active' && !transcript.length && !audioModel && setTooltip({
        phoneNumber: { step: 4, title: 'Call the number', text: 'Grab a phone and call this number to test your agent\'s response' }
      });
      state === 'active' && !audioModel && transcript.length && options.tts.voice === defaultOptions.tts.voice && setTooltip({
        selectVoice: { step: 5, title: 'Change voices', text: 'Customise the voice your agent uses' }
      });
      state === 'active' && (audioModel || (transcript.length && options.tts.voice !== defaultOptions.tts.voice)) && setTooltip({
        done: true,
      });

    }
  }, [audioModel, modelName, prompt, state, transcript, options, tooltip.done, setTooltip]);

  useEffect(() => {
    let [provider, voiceName] = voice?.split(':') || [];
    language && voiceName && provider && setOptions(o => ({
      ...o,
      tts: {
        voice: voiceName,
        vendor: provider
      },
      stt: {
        language,
        ...(sttVendor ? { vendor: sttVendor } : {}),
      }
    }));

  }, [language, voice, sttVendor]);

  // Helper function to build complete options object including fallback
  const buildOptionsWithFallback = useCallback(() => {
    const fallback =
      enableFailover && (fallbackModel || fallbackNumber)
        ? {
            ...(fallbackModel ? { model: fallbackModel } : {}),
            ...(fallbackNumber ? { number: fallbackNumber } : {}),
          }
        : undefined;

    // TEST CODE: Automatically add Ultravox vendor-specific options for Ultravox models
    // This adds experimentalSettings for Deepgram Nova 3 transcription
    // To test with Ultravox transcription instead, change "deepgram-nova-3" to "ultravox"
    // To test with GLM 4.6 model, use model "fixie-ai/ultravox-glm4.6-355b-preview" with transcriptionProvider: "ultravox"
    const vendorSpecific = modelName && modelName.includes('ultravox')
      ? {
          ultravox: {
            experimentalSettings: {
              transcriptionProvider: "deepgram-nova-3"  // Change to "ultravox" to test Ultravox transcription
            }
          }
        }
      : undefined;

    // Apply greeting state into the options payload (omit if none or blank).
    const greeting =
      greetingKind === 'none' || !String(greetingValue || '').trim()
        ? undefined
        : greetingKind === 'instructions'
        ? { instructions: String(greetingValue) }
        : { text: String(greetingValue) };

    const built = {
      ...options,
      ...(greeting ? { greeting } : {}),
      temperature,
      voice: audioModel && voice?.replace(/.*:/, ''),
      ...(fallback ? { fallback } : {}),
      ...(vendorSpecific ? { vendorSpecific } : {}),
    };
    // Auxiliary transcription — only for models that accept options.stt.aux
    // (the catalogue's hasAuxStt); other models keep whatever they stored.
    const withAux = agents?.[modelName]?.hasAuxStt ? withAuxStt(built, auxStt) : built;
    return agents?.[modelName]?.hasOutputStt ? withOutputStt(withAux, outputStt) : withAux;
  }, [options, temperature, audioModel, voice, enableFailover, fallbackModel, fallbackNumber, modelName, greetingKind, greetingValue, agents, auxStt, outputStt]);

  // Live duplicate-name highlight: a debounced server-side name search, so we
  // never hold the full agent list client-side. `search` is a case-insensitive
  // substring on the server, so we still exact-match on the normalized key after
  // fetching ("Bob" would otherwise match "Bobby"). The cleanup cancels stale
  // in-flight requests so rapid typing settles on the latest result only.
  useEffect(() => {
    const key = normalizeAgentDisplayNameKey(metaData.name);
    if (status !== 'loggedIn' || !key) {
      setNameIsDuplicate(false);
      return;
    }
    const excludeId = agentDefinitionId || agent?.id || null;
    let cancelled = false;
    const t = setTimeout(async () => {
      try {
        const data = await listAgents({ search: metaData.name, searchField: 'name', limit: 200 });
        if (cancelled) return;
        const rows = Array.isArray(data?.agents) ? data.agents : [];
        setNameIsDuplicate(
          rows.some(
            (a) => (excludeId == null || a.id !== excludeId) && normalizeAgentDisplayNameKey(a.name) === key,
          ),
        );
      } catch {
        if (!cancelled) setNameIsDuplicate(false);
      }
    }, 400);
    return () => {
      cancelled = true;
      clearTimeout(t);
    };
  }, [metaData.name, agentDefinitionId, agent?.id, status]);

  /** Save-time duplicate-name gate: paginated server name search, holds nothing. */
  const ensureNoDuplicateAgentName = useCallback(
    async ({ name: nameToCheck = metaData.name, excludeAgentId } = {}) => {
      const key = normalizeAgentDisplayNameKey(nameToCheck);
      if (!key) return true;
      const ex =
        excludeAgentId !== undefined ? excludeAgentId : agentDefinitionId || agent?.id || undefined;
      try {
        // Server `search` is a case-insensitive substring, so page through every
        // match and exact-match on the normalized key: a common substring (e.g.
        // "Agent") could otherwise push the real duplicate past a single window.
        let offset = 0;
        for (;;) {
          const data = await listAgents({ search: nameToCheck, searchField: 'name', limit: 200, offset });
          const rows = Array.isArray(data?.agents) ? data.agents : [];
          const conflict = rows.find((a) => {
            if (ex != null && a.id === ex) return false;
            return normalizeAgentDisplayNameKey(a.name) === key;
          });
          if (conflict) {
            setError('An agent with this name already exists.');
            return false;
          }
          const next = data?.next;
          if (typeof next !== 'number' || next <= offset) break;
          offset = next;
        }
      } catch (err) {
        setError(`Could not verify agent name: ${formatAgentApiError(err)}`);
        return false;
      }
      return true;
    },
    [metaData.name, agentDefinitionId, agent, setError],
  );

  useEffect(() => {
    if (state === 'active' && agent?.id) {
      updateAgent({ id: agent.id, options: buildOptionsWithFallback() });
    }
  }, [options, state, agent, buildOptionsWithFallback]);

  useEffect(() => {
    console.log({ agents, modelName, agent: agents[modelName], functions: agents[modelName]?.supportsFunctions }, 'useEffect modelname');
    setFunctionSupport(agents?.[modelName]?.supportsFunctions);
    setMcpSupport(agents?.[modelName]?.supportsMcp);
    setAudioModel(agents?.[modelName]?.audioModel);
  }, [modelName, agents]);


  // The speaker of a transcript message is its one non-envelope key
  // ('user', 'agent', 'user-aux', ...).
  const speakerOf = (m) => Object.keys(m || {}).find((k) => !['isFinal', 'callId', 'timestamp'].includes(k));
  const onMessage = useCallback((message) => {
    console.log({ message, isArray: Array.isArray(message) }, 'onMessage');
    Array.isArray(message) ?
      setTranscript(message) :
      setTranscript((t) => {
        let existing = (Array.isArray(t) && t) || [];
        !message.hasOwnProperty('isFinal') && (message.isFinal = true);
        console.log({ existing, length: existing.length, lastIsFinal: existing.length < 1 || existing[existing.length - 1].isFinal }, 'onMessage existing');
        // Append unless the last item is the SAME speaker still streaming — a
        // final from another speaker (e.g. the auxiliary STT's `user-aux`
        // reading arriving mid-way through the primary `user` line) must not
        // overwrite the line in flight.
        const last = existing[existing.length - 1];
        (
          (existing.length < 1 || last.isFinal || speakerOf(last) !== speakerOf(message))
          && last !== message
        )
          ? existing.push(message)
          : (existing[existing.length - 1] = message);
        console.log({ existing }, 'onMessage returning');
        return [...existing];
      });
  }, []);

  const buttonClick = async (callType) => {
    try {
      if (state === 'initial' && !agent?.id) {
        // For inbound calls, show phone number selector first
        if (callType === 'inbound-call') {
          setPendingCallType(callType);
          setShowPhoneSelector(true);
          return;
        }

        // For outbound calls, show outbound setup first
        if (callType === 'outbound-call') {
          setPendingCallType(callType);
          setShowOutboundSetup(true);
          return;
        }

        if (!(await ensureNoDuplicateAgentName())) {
          return;
        }

        setState('trying');
        let res = await createAgent({
          agentId: agentDefinitionId,
          name: metaData.name,
          description: metaData.description,
          modelName,
          prompt: prompt.value,
          options: buildOptionsWithFallback(),
          functions: functionSupport && functions,
          mcpServers: mcpSupport && mcpServers,
          keys: apiKeys,
          onMessage,
          onClose: () => setWs(null),
          audioModel,
          mediaType: callType,
          phoneNumber: selectedPhoneNumber,
          endpointId: selectedEndpointId,
        });
        setPrompt((p) => ({ ...p }));
        setChanged(false);
        if (res?.id && !agentDefinitionId) setAgentDefinitionId(res.id);
        setWs(res.ws);
        setAgent(res);
        setState('active');
        setTranscript([]);
        
        if (callType === 'inbound-call') {
          setInform({ success: `Agent created and handling calls to ${res?.number}` });
        } else if (callType === 'outbound-call') {
          setInform({ success: `Agent created and will call you back` });
        } else {
          setInform({ success: `Starting agent to talk to you, enable microphone!` });
        }

      }
      else if (state === 'active' && agent?.id) {
        if (!(await ensureNoDuplicateAgentName())) {
          return;
        }
        await updateAgent({
          id: agent.id,
          name: metaData.name,
          description: metaData.description,
          modelName,
          prompt: prompt.value,
          options: buildOptionsWithFallback(),
          functions: functionSupport && functions,
          mcpServers: mcpSupport && mcpServers,
          keys: apiKeys
        });
        setChanged(false);
        setInform({ success: 'Agent updated, will take effect from start of next call' });
      }
    }
    catch (err) {
      setState('initial');
      setError(`Couldn't ${state === 'initial' ? 'create' : 'update'} agent: ${formatAgentApiError(err)}`);
      console.log(err);
    }
  };

  const snapshot = useCallback(() => ({
    id: agentDefinitionId ?? agent?.id,
    name: metaData.name,
    description: metaData.description,
    prompt: typeof prompt?.value === 'string' ? prompt : { value: prompt?.value ?? '', changed: false, changedSinceCreate: false },
    modelName,
    language,
    voice,
    functions,
    mcpServers,
    options: { ...options, temperature },
    // Kept explicitly: `options` above never carries the aux block (it is
    // applied at save time), and an absent block reads as ON — so a snapshot
    // could not otherwise remember that the user switched it off.
    auxStt,
    outputStt,
    keys: apiKeys
  }), [agentDefinitionId, agent?.id, metaData, prompt, modelName, language, voice, functions, mcpServers, options, temperature, auxStt, outputStt, apiKeys]);

  const restore = useCallback((data) => {
    const promptValue = typeof data.prompt === 'string' ? data.prompt : data.prompt?.value;
    setPrompt({
      value: promptValue ?? '',
      changed: true,
      changedSinceCreate: true
    });
    if (data.modelName != null) setModelName(data.modelName);
    if (data.options != null) {
      setOptions(data.options);
      if (data.options.temperature != null) setTemperature(data.options.temperature);
    }
    setAuxStt(typeof data.auxStt === 'boolean' ? data.auxStt : auxSttFromOptions(data.options));
    setOutputStt(typeof data.outputStt === 'boolean' ? data.outputStt : outputSttFromOptions(data.options));
    // Greeting UI state (keep the textarea visible even when empty).
    try {
      const g = data.options?.greeting;
      if (g?.text != null) {
        setGreetingKind('text');
        setGreetingValue(String(g.text));
      } else if (g?.instructions != null) {
        setGreetingKind('instructions');
        setGreetingValue(String(g.instructions));
      } else {
        setGreetingKind('none');
        setGreetingValue('');
      }
    } catch {
      setGreetingKind('none');
      setGreetingValue('');
    }
    if (data.language != null) setLanguage(data.language);
    if (data.voice != null) setVoice(data.voice);
    if (data.functions != null) setFunctions(data.functions);
    if (data.mcpServers != null) setMcpServers(data.mcpServers);
    if (data.keys != null) setApiKeys(data.keys);
    setMetaData({ name: data.name ?? '', description: data.description ?? '' });
    if (data.id != null) setAgentDefinitionId(data.id);
    else setAgentDefinitionId(null);
  }, []);

  const resetEditorAfterAgentDeleted = useCallback(() => {
    setAgentDefinitionId(null);
    setSelectedAgentId?.(null);
    setMetaData({ name: '', description: '' });
    setPrompt({ value: '', changed: false, changedSinceCreate: false });
    setModelName(undefined);
    setFunctions([]);
    setMcpServers([]);
    setApiKeys([]);
    setOptions({ ...defaultOptions });
    setTemperature(0.2);
    setLanguage(defaultOptions.tts.language);
    setVoice('google:en-GB-Wavenet-A');
    setGreetingKind('none');
    setGreetingValue('');
    setSttVendor(defaultOptions.stt.vendor);
    setSttProviders([]);
    setAuxStt(true);
    setOutputStt(false);
    setChanged(false);
    setAgent({});
    setTranscript(undefined);
    setTooltip({});
    setFallbackModel('');
    setFallbackNumber('');
    setShowPhoneSelector(false);
    setShowOutboundSetup(false);
    setPendingCallType(null);
    setSelectedPhoneNumber(null);
  }, [setSelectedAgentId]);

  const closeAgent = useCallback(() => {
    resetEditorAfterAgentDeleted();
    setInform?.({ success: 'Editor cleared.' });
  }, [resetEditorAfterAgentDeleted, setInform]);

  const saveAgent = useCallback(async () => {
    try {
      if (!(await ensureNoDuplicateAgentName())) {
        return;
      }
      if (agentDefinitionId) {
        const updated = await updateAgent({
          id: agentDefinitionId,
          name: metaData.name,
          description: metaData.description,
          modelName,
          prompt: prompt.value,
          options: buildOptionsWithFallback(),
          functions: functionSupport ? functions : undefined,
          mcpServers: mcpSupport ? mcpServers : undefined,
          keys: apiKeys
        });
        setChanged(false);
        setSelectedAgentId?.(agentDefinitionId);
        setInform({ success: 'Agent saved.' });
        return updated;
      }
      const created = await createAgentDefinition({
        name: metaData.name,
        description: metaData.description,
        modelName,
        prompt: prompt.value,
        options: buildOptionsWithFallback(),
        functions: functionSupport ? functions : undefined,
        mcpServers: mcpSupport ? mcpServers : undefined,
        keys: apiKeys
      });
      setAgentDefinitionId(created?.id);
      setSelectedAgentId?.(created?.id);
      setChanged(false);
      setInform({ success: 'Agent saved.' });
      return created;
    } catch (err) {
      setError(`Save failed: ${formatAgentApiError(err)}`);
      throw err;
    }
  }, [
    ensureNoDuplicateAgentName,
    agentDefinitionId,
    metaData,
    prompt,
    modelName,
    buildOptionsWithFallback,
    functionSupport,
    functions,
    mcpSupport,
    mcpServers,
    apiKeys,
    setError,
    setInform,
    setSelectedAgentId,
  ]);

  const saveDuplicateAgent = useCallback(async () => {
    try {
      const dupName = (metaData.name || 'Unnamed').replace(/\s*\(copy\)\s*$/i, '') + ' (copy)';
      if (!(await ensureNoDuplicateAgentName({ name: dupName, excludeAgentId: null }))) {
        return;
      }
      const created = await createAgentDefinition({
        name: dupName,
        description: metaData.description,
        modelName,
        prompt: prompt.value,
        options: buildOptionsWithFallback(),
        functions: functionSupport ? functions : undefined,
        mcpServers: mcpSupport ? mcpServers : undefined,
        keys: apiKeys
      });
      setAgentDefinitionId(created?.id);
      setSelectedAgentId?.(created?.id);
      setMetaData((m) => ({ ...m, name: dupName }));
      setChanged(false);
      setInform({ success: 'Duplicate saved.' });
      return created;
    } catch (err) {
      setError(`Save duplicate failed: ${formatAgentApiError(err)}`);
      throw err;
    }
  }, [
    ensureNoDuplicateAgentName,
    metaData,
    prompt,
    modelName,
    buildOptionsWithFallback,
    functionSupport,
    functions,
    mcpSupport,
    mcpServers,
    apiKeys,
    setError,
    setInform,
    setSelectedAgentId,
  ]);

  const loadAgentById = useCallback(async (id) => {
    try {
      const data = await getAgent(id);
      const normalized = {
        ...data,
        prompt: typeof data.prompt === 'string' ? data.prompt : data.prompt?.value,
        options: data.options || defaultOptions
      };
      restore(normalized);
      setSelectedAgentId?.(id);
      setInform({ success: 'Agent loaded.' });
    } catch (err) {
      setError(`Load failed: ${formatAgentApiError(err)}`);
    }
  }, [restore, setError, setInform, setSelectedAgentId]);

  // When switching to Build tab with a different agent selected in Observe, load that agent.
  useEffect(() => {
    if (syncToAgentId && syncToAgentId !== agentDefinitionId) {
      loadAgentById(syncToAgentId);
    }
  }, [syncToAgentId, agentDefinitionId, loadAgentById]);

  const canDownload = !!(
    agentDefinitionId ||
    (metaData.name || '').trim() ||
    modelName ||
    (typeof prompt?.value === 'string' && prompt.value.trim())
  );

  useEffect(() => {
    return registerFileActions({
      loadAgentById,
      closeAgent,
      saveAgent,
      saveDuplicateAgent,
      upload: () => uploadAgentJsonFile(restore, setError),
      download: () => downloadAgentJson(snapshot),
      canSave: changed,
      hasAgentName: !!(metaData.name || '').trim(),
      agentDisplayName: (metaData.name || '').trim(),
      canDownload,
    });
  }, [
    registerFileActions,
    loadAgentById,
    closeAgent,
    saveAgent,
    saveDuplicateAgent,
    restore,
    snapshot,
    setError,
    changed,
    metaData.name,
    canDownload,
    agentDefinitionId,
    modelName,
    prompt?.value,
  ]);

  // Helper function to extract handler from modelName
  const getAgentHandler = (modelName) => {
    if (!modelName) return null;
    // Parse modelName format: handler:provider/model (e.g., jambonz:openai/gpt-4o)
    const match = modelName.match(/^([a-z0-9-_]+):/);
    return match ? match[1] : null;
  };

  const handleTelephonyResourceSelect = async (selection) => {
    const kind = selection?.kind;
    const number = kind === 'phone' ? selection?.number : null;
    const endpointId = kind === 'registration' ? selection?.id : null;
    setSelectedPhoneNumber(number);
    setSelectedEndpointId(endpointId);
    setShowPhoneSelector(false);

    try {
      if (!(await ensureNoDuplicateAgentName())) {
        return;
      }
      setState('trying');
      let res = await createAgent({
        agentId: agentDefinitionId,
        name: metaData.name,
        description: metaData.description,
        modelName,
        prompt: prompt.value,
        options: buildOptionsWithFallback(),
        functions: functionSupport && functions,
        mcpServers: mcpSupport && mcpServers,
        keys: apiKeys,
        onMessage,
        onClose: () => setWs(null),
        audioModel,
        mediaType: pendingCallType,
        phoneNumber: number,
        endpointId,
      });
      setPrompt((p) => ({ ...p }));
      setChanged(false);
      if (res?.id && !agentDefinitionId) setAgentDefinitionId(res.id);
      setWs(res.ws);
      setAgent(res);
      setState('active');
      setTranscript([]);
      setPendingCallType(null);
      setSelectedPhoneNumber(null);
      setSelectedEndpointId(null);

      if (kind === 'registration') {
        setInform({ success: 'Agent created and handling calls via SIP registration' });
      } else {
        setInform({ success: `Agent created and handling calls to ${res?.number}` });
      }
    } catch (err) {
      setState('initial');
      setError(`Couldn't create agent: ${formatAgentApiError(err)}`);
      console.log(err);
    }
  };

  const handleOutboundCallSetup = async ({ callerId, numberToCall, callerKind }) => {
    setShowOutboundSetup(false);
    
    // Now create the agent with the outbound call details
    try {
      if (!(await ensureNoDuplicateAgentName())) {
        return;
      }
      setState('trying');
      let res = await createAgent({
        agentId: agentDefinitionId,
        name: metaData.name,
        description: metaData.description,
        modelName,
        prompt: prompt.value,
        options: buildOptionsWithFallback(),
        functions: functionSupport && functions,
        mcpServers: mcpSupport && mcpServers,
        keys: apiKeys,
        onMessage,
        onClose: () => setWs(null),
        audioModel,
        mediaType: pendingCallType,
        phoneNumber: callerKind === 'phone' ? callerId : undefined,
        endpointId: callerKind === 'registration' ? callerId : undefined,
        outboundNumber: numberToCall
      });
      setPrompt((p) => ({ ...p }));
      setChanged(false);
      if (res?.id && !agentDefinitionId) setAgentDefinitionId(res.id);
      setWs(res.ws);
      setAgent(res);
      setState('active');
      setTranscript([]);
      setPendingCallType(null);

      setInform({ success: `Agent created and will call ${numberToCall} from ${callerId}` });
    } catch (err) {
      setState('initial');
      setError(`Couldn't create agent: ${formatAgentApiError(err)}`);
      console.log(err);
    }
  };



  useComponentWillUnmount(() => disconnect());

  return (
    <>
      <Grid container spacing={2} sx={{ flexGrow: 1, width: '100%' }}>
        <Grid xs={12}>
          <Item>
            <Grid container spacing={1}>
              <Grid xs={12} md={6}>
                <Box>
                  <Input
                    placeholder="Agent name"
                    value={metaData.name ?? ''}
                    onChange={(e) => {
                      setMetaData((m) => ({ ...m, name: e.target.value }));
                      setChanged(true);
                    }}
                    size="sm"
                    color={nameIsDuplicate ? 'danger' : 'neutral'}
                    aria-invalid={nameIsDuplicate || undefined}
                    sx={{ width: '100%' }}
                  />
                  {nameIsDuplicate && (
                    <Typography level="body-xs" sx={{ mt: 0.5 }} color="danger">
                      Another agent already uses this name.
                    </Typography>
                  )}
                </Box>
              </Grid>
              <Grid xs={12} md={6}>
                <Textarea
                  placeholder="Agent description"
                  value={metaData.description ?? ''}
                  onChange={(e) => {
                    setMetaData((m) => ({ ...m, description: e.target.value }));
                    setChanged(true);
                  }}
                  minRows={1}
                  size="sm"
                  sx={{ width: '100%' }}
                />
              </Grid>
            </Grid>
          </Item>
        </Grid>
        <Grid xs={12} sm={6}>
          <Item>
            <SelectAgent
              disabled={state !== 'initial'}
              options={agents}
              placeholder="Select model"
              tooltip={onboardingTooltip('selectAgent')}
              {...{ modelName, agents, setModelName, setFunctionSupport, setAudioModel }}
            />
          </Item>
          <Item>
            <Grid container xs={12} sm={12}>
              <Grid xs={12} md={8}>
                <TemperatureSlider value={temperature} setValue={setTemperature} />
              </Grid>
              <Grid xs={12} md={4} alignContent="center">
                <FunctionCalls {...{ functions, setFunctions, mcpServers, setMcpServers, mcpSupport, apiKeys, setApiKeys }} disabled={!(functionSupport || mcpSupport)} sx={{ width: '100%', ml: 3 }} />
              </Grid>
            </Grid>
          </Item>
          {/* Greeting moved under PromptInput (left column, lower section) */}
        </Grid>
        <Grid container xs={12} sm={6}>
          <Grid xs={12} md={6}>
            <Item>
              <AgentButton
                disabled={
                  state === 'trying' ||
                  !modelName ||
                  !prompt?.value?.length ||
                  (state === 'active' && !changed)
                }
                audioModel={audioModel}
                hasTelephony={agents?.[modelName]?.hasTelephony}
                hasWebRTC={agents?.[modelName]?.hasWebRTC}
                onClick={buttonClick} state={state}
                tooltip={onboardingTooltip('agentButton')}
                sx={{ width: '100%' }}
              />
            </Item>
          </Grid>
          <Grid xs={12} md={6}>
            <Item>
              {state === 'active' ?
                (<Button disabled={state !== 'active'} color="danger" onClick={disconnect} sx={{ width: '100%' }}>
                  Disconnect Agent
                </Button>) :
                (<AgentPersistenceControls
                  onAgentDeleted={resetEditorAfterAgentDeleted}
                  agentDefinitionId={agentDefinitionId}
                  sx={{ width: '100%' }}
                  setError={setError}
                  setInform={setInform}
                />)
              }
            </Item>
          </Grid>
          {enableFailover && (
            <Grid xs={12}>
              <Item>
                <Grid container spacing={1}>
                  <Grid xs={12} md={6}>
                    {/* Fallback model dropdown – reuse SelectAgent styling/behavior */}
                    <SelectAgent
                      disabled={state !== 'initial'}
                      options={agents}
                      placeholder="Fallback model (optional)"
                      tooltip={null}
                      modelName={fallbackModel}
                      agents={agents}
                      setModelName={setFallbackModel}
                      // No side-effects for fallback selection
                      setFunctionSupport={() => {}}
                      setAudioModel={() => {}}
                      allowEmpty={true}
                      emptyOptionLabel="No fallback model"
                    />
                  </Grid>
                  <Grid xs={12} md={6}>
                    <Input
                      fullWidth
                      placeholder="Fallback transfer number (E.164 or endpoint ID)"
                      value={fallbackNumber}
                      onChange={(e) => setFallbackNumber(e.target.value)}
                      size="sm"
                    />
                  </Grid>
                </Grid>
              </Item>
            </Grid>
          )}
          <Grid container xs={12}>
            <SelectVoice
              {...{
                language,
                modelName,
                setLanguage,
                defaultLanguage: defaultOptions.tts.language,
                voice,
                voices,
                setVoice,
                localeChoices: voiceLocales,
                sttProviders,
                sttVendor,
                setSttVendor,
              }}
              tooltip={onboardingTooltip('selectVoice')}
            />
          </Grid>
          <Grid xs={12}>
            <AuxSttToggle
              hasAuxStt={!!agents?.[modelName]?.hasAuxStt}
              auxStt={auxStt}
              setAuxStt={(v) => { setAuxStt(v); setChanged(true); }}
              hasOutputStt={!!agents?.[modelName]?.hasOutputStt}
              outputStt={outputStt}
              setOutputStt={(v) => { setOutputStt(v); setChanged(true); }}
            />
          </Grid>
        </Grid>
        <Grid xs={12} sm={6}>
          <Item>
            <PromptInput
              prompt={prompt}
              setPrompt={(prompt) => {
                setPrompt(prompt);
                setChanged(true);
              }}
              modelName={modelName}
              agents={agents}
              tooltip={onboardingTooltip('promptInput')}
              endDecorator={<ExampleFiles setData={restore} setError={setError} disabled={prompt?.value?.length >= 100} />} />
          </Item>
          <Item>
            <Select
              placeholder="Greeting"
              value={greetingKind}
              onChange={(_, value) => {
                const next = String(value || 'none');
                setGreetingKind(next);
                setChanged(true);
                applyGreetingToOptions(next, greetingValue);
              }}
              size="sm"
              sx={{ width: '100%' }}
            >
              <Option value="none">Greeting: None</Option>
              <Option value="text">Greeting: Text</Option>
              <Option value="instructions">Greeting: Instructions</Option>
            </Select>
            {greetingKind !== 'none' && (
              <Textarea
                minRows={2}
                placeholder={
                  greetingKind === 'text'
                    ? 'Greeting text (played verbatim)'
                    : 'Greeting instructions (LLM will generate)'
                }
                value={greetingValue}
                onChange={(e) => {
                  const v = e.target.value;
                  setGreetingValue(v);
                  applyGreetingToOptions(greetingKind, v);
                }}
                sx={{ mt: 1, width: '100%' }}
              />
            )}
          </Item>

        </Grid>
        <Grid xs={12} sm={6}>
          <Item>
            <Transcript
              transcript={transcript}
              number={agent?.number}
              tooltip={onboardingTooltip('phoneNumber')}
              state={state}
              audioModel={audioModel}
              disconnect={disconnect}
            />
          </Item>

        </Grid>
        {audioModel && state === 'active' &&
          <Box
            sx={{
              position: 'fixed',
              bottom: 10,
              right: 10,
            }}
          >
            <Tooltip
              title="End Call"
              placement="top-end"
              color="danger"

            >
              <Fab
                color="error"
                aria-label="hangup"
                onClick={() => disconnect()}
              >
                <CallEndIcon />
              </Fab>
            </Tooltip>
          </Box>}
      </Grid>

      <TelephonyResourceSelector
        open={showPhoneSelector}
        onClose={() => {
          setShowPhoneSelector(false);
          setPendingCallType(null);
        }}
        onSelect={handleTelephonyResourceSelect}
        loading={state === 'trying'}
        agentHandler={getAgentHandler(modelName)}
      />

      <OutboundCallSetup
        open={showOutboundSetup}
        onClose={() => {
          setShowOutboundSetup(false);
          setPendingCallType(null);
        }}
        onSetup={handleOutboundCallSetup}
        loading={state === 'trying'}
        agentHandler={getAgentHandler(modelName)}
      />


    </>
  );
};